import React from "react"
import styled from "styled-components"
import colors from "@constants/colors"
import breakpoints from "@constants/breakpoints"
import { body14Styles } from "@constants/typography"
import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import { isNilOrEmpty } from "@utils/index"
import { MobileOnly, TabletAndDesktopOnly } from "@utils/MediaQuery"

const Container = styled.div`
  padding: 0 20px 90px;

  @media ${breakpoints.tablet} {
    padding: 0 calc(1 / 12 * 100vw) 208px;
  }

  @media ${breakpoints.laptop} {
    padding: 0 calc(1 / 12 * 100vw) 224px calc(2 / 12 * 100vw);
  }
`

const Content = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`

const FirstColumn = styled.div`
  width: calc(1 / 2 * (100vw - 60px));
  flex-grow: 0;
  flex-shrink: 0;

  @media ${breakpoints.tablet} {
    width: calc((1 / 9 + 3) / 12 * 100vw);
  }

  @media ${breakpoints.laptop} {
    width: calc(3 / 12 * 100vw - 36px);
  }
`

const SecondColumn = styled.div`
  width: calc(1 / 2 * (100vw - 60px));
  flex-grow: 0;
  flex-shrink: 0;

  @media ${breakpoints.tablet} {
    width: calc((2 / 9 + 3) / 12 * 100vw);
  }

  @media ${breakpoints.laptop} {
    width: calc(3 / 12 * 100vw - 36px);
  }
`

const ThirdColumn = styled.div`
  width: calc((1 / 9 + 3) / 12 * 100vw);
  flex-grow: 0;
  flex-shrink: 0;

  @media ${breakpoints.laptop} {
    width: calc(3 / 12 * 100vw - 36px);
  }
`

const TallImage = styled.div`
  ${ImageHelperContainer} {
    width: calc(1 / 2 * (100vw - 60px));
    height: calc(1 / 2 * (100vw - 60px) * 1.78);
    margin-bottom: 20px;

    @media ${breakpoints.tablet} {
      width: calc((2 / 9 + 3) / 12 * 100vw);
      height: calc((2 / 9 + 3) / 12 * 100vw * 1.73);
      margin-bottom: 47px;
    }

    @media ${breakpoints.laptop} {
      width: calc(3 / 12 * 100vw - 36px);
      height: calc((3 / 12 * 100vw - 36px) * 1.73);
      margin-bottom: 39px;
    }
  }
`

const MediumImage = styled.div`
  ${ImageHelperContainer} {
    width: calc(1 / 2 * (100vw - 60px));
    height: calc(1 / 2 * (100vw - 60px));
    margin-bottom: 20px;

    @media ${breakpoints.tablet} {
      width: calc((1 / 9 + 3) / 12 * 100vw);
      height: calc((1 / 9 + 3) / 12 * 100vw);
      margin-bottom: 24px;
    }

    @media ${breakpoints.laptop} {
      width: calc(3 / 12 * 100vw - 36px);
      height: calc(3 / 12 * 100vw - 36px);
      margin-bottom: 48px;
    }
  }
`

const ShortImage = styled.div`
  ${ImageHelperContainer} {
    width: calc((1 / 9 + 3) / 12 * 100vw);
    height: calc((1 / 9 + 3) / 12 * 100vw * 0.62);

    @media ${breakpoints.laptop} {
      width: calc(3 / 12 * 100vw - 36px);
      height: calc((3 / 12 * 100vw - 36px) * 0.62);
    }
  }
`

const CaptionsWrapper = styled.div`
  @media ${breakpoints.tablet} {
    display: flex;
  }

  @media ${breakpoints.laptop} {
    display: flex;
  }
`

const Captions = styled.div`
  display: flex;
  flex-direction: column;
`

const Caption = styled.div`
  font-family: "Avenir Roman";
  font-size: 15px;
  color: ${colors.black};
  line-height: 22px;
  opacity: 0.3;

  @media ${breakpoints.tablet} {
    ${body14Styles}
    color: ${colors.mediumGray};
    opacity: 1;
  }

  @media ${breakpoints.laptop} {
    ${body14Styles}
    color: ${colors.mediumGray};
    opacity: 1;
  }
`

const Dash = styled.div`
  width: 23px;
  height: 1px;
  margin: 9px 12px 0 0;
  background-color: ${colors.mediumGray};
`

interface ImageGridProps {
  blockId?: string
  images: [string, string, string, string, string]
  captions: Array<string>
}

const ImageGrid: React.FC<ImageGridProps> = ({ blockId, images, captions }) => {
  if (images.length !== 5) {
    return null
  }

  const MobileGrid = () => (
    <>
      <Content>
        <FirstColumn>
          <MediumImage>
            <ImageHelper src={images[0]} />
          </MediumImage>
          <MediumImage>
            <ImageHelper src={images[1]} />
          </MediumImage>
          <MediumImage>
            <ImageHelper src={images[3]} />
          </MediumImage>
        </FirstColumn>
        <SecondColumn>
          <TallImage>
            <ImageHelper src={images[2]} />
          </TallImage>
          <MediumImage>
            <ImageHelper src={images[4]} />
          </MediumImage>
        </SecondColumn>
      </Content>
      <CaptionsWrapper>
        <Captions>
          {!isNilOrEmpty(captions) &&
            captions.map((caption) => (
              <Caption key={`mobile-${caption}`}>{caption}</Caption>
            ))}
        </Captions>
      </CaptionsWrapper>
    </>
  )

  const TabletAndDesktopGrid = () => (
    <Content>
      <FirstColumn>
        <MediumImage>
          <ImageHelper src={images[0]} />
        </MediumImage>
        <MediumImage>
          <ImageHelper src={images[1]} />
        </MediumImage>
      </FirstColumn>
      <SecondColumn>
        <TallImage>
          <ImageHelper src={images[2]} />
        </TallImage>
        <CaptionsWrapper>
          <Dash />
          <Captions>
            {!isNilOrEmpty(captions) &&
              captions.map((caption) => (
                <Caption key={`mobile-${caption}`}>{caption}</Caption>
              ))}
          </Captions>
        </CaptionsWrapper>
      </SecondColumn>
      <ThirdColumn>
        <MediumImage>
          <ImageHelper src={images[3]} />
        </MediumImage>
        <ShortImage>
          <ImageHelper src={images[4]} />
        </ShortImage>
      </ThirdColumn>
    </Content>
  )

  return (
    <Container id={blockId || undefined}>
      <MobileOnly>
        <MobileGrid />
      </MobileOnly>
      <TabletAndDesktopOnly>
        <TabletAndDesktopGrid />
      </TabletAndDesktopOnly>
    </Container>
  )
}

export default ImageGrid
